import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import Pill from "../../../common/Pill/Pill";
import CustomText from "../../../common/general/CustomText/CustomText";
import {
  getConnectorConnectionPattern,
  hasSavedConfiguration,
  type CatalogConnector,
  type ConnectionStatus,
} from "../catalog.utils";
import { ConnectorLogo } from "./ConnectorLogo";
import { PILL_TYPES } from "./ConnectorDetails";

interface ConnectorDetailsHeaderProps {
  connector: CatalogConnector;
  connectionStatus: ConnectionStatus | null;
  hasApproval: boolean;
  onClose: () => void;
}

export function ConnectorDetailsHeader({
  connector,
  connectionStatus,
  hasApproval,
  onClose,
}: ConnectorDetailsHeaderProps) {
  const connectionPattern = getConnectorConnectionPattern(connector);
  const isConnected = connectionStatus !== null || hasSavedConfiguration(connector);

  return (
    <View style={styles.header}>
      <ConnectorLogo iconKey={connector.icon} category={connector.category} size={40} />

      <View style={styles.headerMeta}>
        <View style={styles.titleRow}>
          <CustomText
            text={connector.name}
            size="m"
            useTranslationText={false}
            textType="text"
            weight="bold"
            role="text"
            style={styles.connectorName}
            numberOfLines={1}
          />
          {isConnected && connectionStatus && (
            <Pill
              text={PILL_TYPES[connectionStatus].text}
              backgroundColor={PILL_TYPES[connectionStatus].backgroundColor}
              textColor={PILL_TYPES[connectionStatus].textColor}
              textSize="xs"
              updated={true}
              borderColor={PILL_TYPES[connectionStatus].borderColor}
              borderWidth={1}
              icon={PILL_TYPES[connectionStatus].icon}
            />
          )}
          {!isConnected && hasApproval && (
            <Pill
              text={PILL_TYPES.approved.text}
              backgroundColor={PILL_TYPES.approved.backgroundColor}
              textColor={PILL_TYPES.approved.textColor}
              textSize="xs"
              updated={true}
              borderColor={PILL_TYPES.approved.borderColor}
              borderWidth={1}
              icon={PILL_TYPES.approved.icon}
            />
          )}
        </View>

        <View style={styles.pillRow}>
          <Pill
            text={PILL_TYPES[connector.category].text}
            backgroundColor={PILL_TYPES[connector.category].backgroundColor}
            textColor={PILL_TYPES[connector.category].textColor}
            textSize="xs"
            updated={true}
            borderColor={PILL_TYPES[connector.category].borderColor}
            borderWidth={1}
            icon={PILL_TYPES[connector.category].icon}
            containerStyle={{ borderRadius: 8 }}
          />

          <View style={styles.patternSpacer} />
          <Pill
            text={isConnected || hasApproval ? "Setup Complete" : PILL_TYPES[connectionPattern].text}
            backgroundColor={PILL_TYPES[connectionPattern].backgroundColor}
            textColor={PILL_TYPES[connectionPattern].textColor}
            textSize="xs"
            updated={true}
            borderColor={PILL_TYPES[connectionPattern].borderColor}
            borderWidth={1}
            icon={PILL_TYPES[connectionPattern].icon}
          />
        </View>
      </View>

      <TouchableOpacity
        onPress={onClose}
        style={styles.closeBtn}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel="Close connector details">
        <Text style={styles.closeBtnText}>✕</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
  },
  headerMeta: {
    flex: 1,
    minWidth: 0,
    gap: 6,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  connectorName: {
    color: "#101828",
    flexShrink: 1,
  },
  pillRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
  },
  patternSpacer: {
    width: 6,
  },
  closeBtn: {
    position: "absolute",
    top: 20,
    right: 20,
    alignItems: "center",
    justifyContent: "center",
  },
  closeBtnText: {
    fontSize: 14,
    color: "#6B7280",
    fontWeight: "600",
  },
});
